import React, { useEffect, useState } from "react";
import { useAuth } from "../lib/auth";
import api from "../lib/api";
import { Link } from "react-router-dom";
import { toast } from "sonner";

const EXAM_TYPE_LABEL = { LGS: "LGS", TYT: "TYT", YKS: "YKS" };
const EDU_LABEL = {
  "9": "9. Sınıf", "10": "10. Sınıf", "11": "11. Sınıf",
  "12": "12. Sınıf", GRADUATE: "Mezun", OTHER: "Diğer",
};
const GRADES = ["9", "10", "11", "12", "GRADUATE"];

export default function Profile() {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [editing, setEditing] = useState(false);
  const [target, setTarget] = useState(null);
  const [grade, setGrade] = useState(null);
  const [busy, setBusy] = useState(false);
  const [joinCode, setJoinCode] = useState("");
  const [myClasses, setMyClasses] = useState([]);

  const loadClasses = async () => {
    const { data } = await api.get("/classes/my");
    setMyClasses(data);
  };

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/me/profile");
        setProfile(r.data);
      } catch { setProfile({}); }
      loadClasses().catch(() => {});
    })();
  }, []);

  const joinClass = async () => {
    if (!joinCode.trim()) return;
    try {
      const { data } = await api.post("/classes/join", { code: joinCode });
      toast.success(`"${data.class_name}" sınıfına katıldın (Öğretmen: ${data.teacher_name}).`);
      setJoinCode("");
      loadClasses();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sınıfa katılınamadı");
    }
  };

  const leaveClass = async (c) => {
    if (!window.confirm(`"${c.class_name}" sınıfından ayrılmak istediğine emin misin?`)) return;
    try {
      await api.delete(`/classes/my/${c.class_id}`);
      toast.success("Sınıftan ayrıldın.");
      loadClasses();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Ayrılınamadı");
    }
  };

  const startEdit = () => {
    setTarget(profile?.exam_type || null);
    setGrade(profile?.education_status || null);
    setEditing(true);
  };

  const save = async () => {
    if (!target || (target !== "LGS" && !grade)) return;
    setBusy(true);
    try {
      const payload = { exam_type: target };
      if (target !== "LGS") payload.education_status = grade;
      const { data } = await api.patch("/me/profile", payload);
      setProfile((prev) => ({ ...prev, ...data }));
      setEditing(false);
      toast.success("Sınav hedefin güncellendi");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Güncellenemedi");
    } finally { setBusy(false); }
  };

  const p = profile || {};
  return (
    <div className="space-y-4">
      <h1 className="font-display text-3xl tracking-tight text-[#0B1A35]">Profil</h1>

      <div className="bg-white border border-[#E2E8F0] rounded-xl p-5">
        <Row label="Ad Soyad" value={user?.name} />
        <Row label="E-posta" value={user?.email} />
        <Row label="Rol" value={user?.role === "admin" ? "Yönetici" : user?.role === "teacher" ? "Öğretmen" : "Öğrenci"} />
        <Row label="Hazırlanılan Sınav"
             value={p.exam_type ? EXAM_TYPE_LABEL[p.exam_type] : (p.exam_level || "Belirtilmedi")} />
        {(p.exam_type === "YKS" || p.exam_type === "TYT") && (
          <Row label="Sınıf" value={EDU_LABEL[p.education_status] || "Belirtilmedi"} />
        )}
        <Row label="Üyelik" value={user?.plan === "premium" ? "Premium" : "Ücretsiz"} />
        {user?.role !== "admin" && !editing && (
          <button onClick={startEdit} data-testid="profile-edit-exam-target-btn"
                  className="yz-secondary mt-3 px-4 py-2 rounded-lg text-sm">
            Sınav Hedefini Düzenle
          </button>
        )}
        {editing && (
          <div data-testid="profile-exam-target-editor" className="mt-4 space-y-3 border-t border-slate-100 pt-4">
            <div className="flex gap-2">
              {["LGS", "TYT", "YKS"].map((t) => (
                <button key={t} onClick={() => setTarget(t)}
                        data-testid={`profile-target-${t.toLowerCase()}`}
                        className={`flex-1 p-3 rounded-xl border-2 font-medium transition-colors ${
                          target === t
                            ? "yz-chip-active"
                            : "border-[#E2E8F0] hover:border-[#0B1A35]"
                        }`}>
                  {t}
                </button>
              ))}
            </div>
            {(target === "TYT" || target === "YKS") && (
              <div className="flex flex-wrap gap-2">
                {GRADES.map((g) => (
                  <button key={g} onClick={() => setGrade(g)}
                          data-testid={`profile-grade-${g.toLowerCase()}`}
                          className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
                            grade === g
                              ? "yz-chip-active"
                              : "border-[#E2E8F0] hover:border-[#0B1A35]"
                          }`}>
                    {EDU_LABEL[g]}
                  </button>
                ))}
              </div>
            )}
            <p className="text-xs text-slate-500">
              Sınav hedefini değiştirmen geçmiş çalışmalarını, oturumlarını ve performans verilerini silmez.
            </p>
            <div className="flex gap-2">
              <button onClick={save} disabled={busy || !target || (target !== "LGS" && !grade)}
                      data-testid="profile-save-exam-target-btn"
                      className="yz-cta px-4 py-2 rounded-lg text-sm disabled:opacity-50">
                {busy ? "Kaydediliyor…" : "Kaydet"}
              </button>
              <button onClick={() => setEditing(false)} data-testid="profile-cancel-edit-btn"
                      className="px-4 py-2 rounded-lg border border-[#E2E8F0] text-sm">
                Vazgeç
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="bg-white border border-[#E2E8F0] rounded-xl p-5">
        <div className="font-display text-lg mb-1">Çalışma Geçmişi</div>
        <p className="text-sm text-slate-500 mb-3">Yanlış cevapladığın soruları gözden geçir ve pratik yap.</p>
        <Link to="/yanlislar"
          data-testid="profile-wrong-attempts-link"
          className="inline-block px-4 py-2 rounded-lg bg-[#0B1A35] text-white text-sm hover:bg-[#16294D] transition-colors">
          Son 50 Yanlışım
        </Link>
      </div>

      {user?.role !== "admin" && (
        <div className="bg-white border border-[#E2E8F0] rounded-xl p-5" data-testid="profile-classes-card">
          <div className="font-display text-lg mb-1">Sınıflarım</div>
          <p className="text-sm text-slate-500 mb-3">
            Öğretmeninin paylaştığı katılım koduyla sınıfa katıl.
          </p>
          <div className="flex gap-2 mb-3">
            <input value={joinCode} onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
              onKeyDown={(e) => e.key === "Enter" && joinClass()}
              placeholder="Katılım kodu"
              data-testid="profile-join-code-input"
              className="flex-1 px-3 py-2 rounded-lg border border-[#E2E8F0] text-sm font-mono tracking-widest outline-none focus:border-[#007BFF] uppercase" />
            <button onClick={joinClass} disabled={!joinCode.trim()}
              data-testid="profile-join-class-btn"
              className="yz-cta px-4 py-2 rounded-lg text-sm disabled:opacity-50">
              Bir Sınıfa Katıl
            </button>
          </div>
          {myClasses.length > 0 ? (
            <ul className="divide-y divide-slate-100" data-testid="profile-my-classes">
              {myClasses.map((c) => (
                <li key={c.membership_id} className="py-2 flex items-center justify-between gap-2"
                    data-testid={`profile-class-row-${c.class_id}`}>
                  <div className="min-w-0">
                    <span className="text-sm font-medium">{c.class_name}</span>
                    {c.is_archived && (
                      <span className="ml-2 px-2 py-0.5 rounded-full bg-slate-200 text-slate-600 text-[10px]">Arşivli</span>
                    )}
                    <div className="text-xs text-slate-500">Öğretmen: {c.teacher_name}</div>
                  </div>
                  <button onClick={() => leaveClass(c)}
                    data-testid={`profile-class-leave-${c.class_id}`}
                    className="px-3 py-1.5 rounded-lg border border-rose-200 text-rose-600 text-xs hover:bg-rose-50">
                    Ayrıl
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-xs text-slate-400 italic" data-testid="profile-no-classes">Henüz bir sınıfa katılmadın.</p>
          )}
        </div>
      )}

      {user?.role !== "admin" && (
        <div className="bg-white border border-[#E2E8F0] rounded-xl p-5" data-testid="profile-teacher-card">
          <div className="font-display text-lg mb-1">Öğretmen misin?</div>
          <p className="text-sm text-slate-500 mb-3">
            Öğretmen paneli için başvur; onaylandığında sınıf özelliklerine erişirsin.
          </p>
          <Link to="/ogretmen-basvuru"
            data-testid="profile-teacher-apply-link"
            className="inline-block px-4 py-2 rounded-lg bg-[#0B1A35] text-white text-sm hover:bg-[#16294D] transition-colors">
            Öğretmen paneline geç
          </Link>
        </div>
      )}

      <div className="bg-white border border-[#E2E8F0] rounded-xl p-5">
        <div className="font-display text-lg mb-3">Premium</div>
        <button disabled className="px-4 py-2 rounded-lg bg-slate-200 text-slate-500 cursor-not-allowed">
          Premium Yakında
        </button>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
      <div className="text-sm text-slate-500">{label}</div>
      <div className="text-sm font-medium">{value || "—"}</div>
    </div>
  );
}
